import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CloudDownload, Loader2, X, FileText, FileImage, File as FileIcon } from "lucide-react";
import ImportReviewDialog from "@/components/ImportReviewDialog";
import { runExtraction } from "@/lib/extract";

const iconFor = (m) => {
  if ((m || "").startsWith("image/")) return FileImage;
  if ((m || "").includes("pdf") || (m || "").includes("word") || (m || "").includes("document")) return FileText;
  return FileIcon;
};

// Legge con l'AI documenti GIÀ presenti nella cartella Drive del cliente (anche più insieme),
// poi mostra la revisione dei campi prima di applicarli all'anagrafica.
export default function DriveExtractButton({ clienteId, currentValues = {}, onExtract }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState({ fatti: 0, totale: 0 });
  const [review, setReview] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["drive-files", clienteId],
    queryFn: async () => (await api.get(`/clienti/${clienteId}/drive-files`)).data,
    enabled: open,
  });
  const files = (data?.files || []).filter((f) => {
    const m = f.mimeType || "";
    return m.startsWith("image/") || m.includes("pdf");
  });
  const nSel = Object.values(sel).filter(Boolean).length;

  const leggi = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (ids.length === 0) return;
    if (ids.length > 8) return toast.error("Massimo 8 documenti per volta.");
    setBusy(true);
    setProg({ fatti: 0, totale: ids.length });
    try {
      const res = await runExtraction({
        url: `/clienti/${clienteId}/estrai-da-drive/start`,
        payload: { file_ids: ids, files: ids.map((id) => ({ id, name: files.find((f) => f.id === id)?.name || "" })) },
        onProgress: setProg,
      });
      const estratti = Object.fromEntries(
        Object.entries(res.fields || {}).filter(([, v]) => String(v || "").trim())
          .map(([k, v]) => [k, String(v).toUpperCase()])
      );
      const infoFiles = (res.files || []).map((f) => ({ ...f, n: Object.keys(f.campi || {}).length }));
      setOpen(false);
      if (Object.keys(estratti).length === 0) {
        toast.warning("Nessun dato riconosciuto nei documenti selezionati.");
      } else {
        setReview({ estratti, infoFiles });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Lettura non riuscita");
    } finally { setBusy(false); setProg({ fatti: 0, totale: 0 }); }
  };

  return (
    <>
      <button type="button" data-testid="drive-extract-btn" onClick={() => { setSel({}); setOpen(true); }}
        className="inline-flex items-center gap-2 text-xs border border-[#C9A84C] text-[#8a6d1f] px-3 py-2 rounded-sm hover:bg-[#faf5e6] transition-colors">
        <CloudDownload className="w-4 h-4" /> Leggi documenti già su Drive (AI)
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[80] bg-[#1a1a1a]/50 flex items-center justify-center p-4" data-testid="drive-extract-dialog">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg border-t-2 border-[#C9A84C] shadow-lg max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between px-6 py-3 border-b border-[#1A2B4A]/10">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-[#4a5568]">Documenti su Drive</p>
                <h3 className="font-serif text-xl text-[#1A2B4A]">Seleziona cosa far leggere all'AI</h3>
              </div>
              <button data-testid="drive-extract-close" onClick={() => setOpen(false)} className="text-[#1A2B4A] hover:text-[#9b2c2c]"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-3">
              {isLoading ? (
                <p className="text-sm text-[#4a5568] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-[#C9A84C]" /> Carico i documenti…</p>
              ) : files.length === 0 ? (
                <p className="text-sm text-[#4a5568]" data-testid="drive-extract-empty">
                  Nessuna immagine o PDF nella cartella Drive di questo cliente: carica prima i documenti.
                </p>
              ) : (
                <ul className="divide-y divide-[#1A2B4A]/5" data-testid="drive-extract-list">
                  {files.map((f) => {
                    const Icon = iconFor(f.mimeType);
                    return (
                      <li key={f.id} className="flex items-center gap-3 py-2.5">
                        <input data-testid={`drive-extract-check-${f.id}`} type="checkbox" checked={!!sel[f.id]}
                          onChange={(e) => setSel({ ...sel, [f.id]: e.target.checked })}
                          className="w-4 h-4 accent-[#C9A84C] shrink-0" />
                        <Icon className="w-4 h-4 text-[#1A2B4A]/50 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-[#1A2B4A] truncate" title={f.name}>{f.name}</div>
                          <div className="text-[11px] text-[#4a5568]">{f.categoria}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-[#1A2B4A]/10">
              {busy && (
                <span data-testid="drive-extract-progress" className="mr-auto text-xs text-[#4a5568]">
                  Lettura in corso… {prog.fatti} di {prog.totale}
                </span>
              )}
              <button data-testid="drive-extract-cancel" onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
              <button data-testid="drive-extract-run" onClick={leggi} disabled={busy || nSel === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudDownload className="w-4 h-4" />} Leggi {nSel || ""} documenti
              </button>
            </div>
          </div>
        </div>, document.body)}

      {review && createPortal(
        <ImportReviewDialog estratti={review.estratti} files={review.infoFiles} currentValues={currentValues}
          onApply={(scelti) => {
            onExtract(scelti);
            toast.success(`${Object.keys(scelti).length} campi applicati all'anagrafica.`);
          }}
          onClose={() => setReview(null)} />, document.body)}
    </>
  );
}
